import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { MdClose } from "react-icons/md";

import { AssociatedClientDecision, AssociatedClientMatchItem } from "types";

type Props = {
  open: boolean;
  items: AssociatedClientMatchItem[];
  canCreate: boolean;
  onCancel: () => void;
  onConfirm: (decisions: AssociatedClientDecision[]) => void;
};

type DecisionState = {
  action: AssociatedClientDecision["action"] | "";
  associatedClientId: number | null;
};

export default function AssociatedClientMatchModal({
  open,
  items,
  canCreate,
  onCancel,
  onConfirm,
}: Props) {
  const [decisions, setDecisions] = useState<Record<string, DecisionState>>({});

  useEffect(() => {
    if (!open) return;

    const initialDecisions = items.reduce<Record<string, DecisionState>>(
      (result, item) => {
        const hasCandidates = item.candidates.length > 0;

        result[item.key] = {
          action: hasCandidates ? "" : "create-new",
          associatedClientId:
            item.candidates[0]?.associatedClient.id ?? null,
        };

        return result;
      },
      {},
    );

    setDecisions(initialDecisions);
  }, [items, open]);

  const completedCount = useMemo(
    () =>
      items.filter((item) => {
        const decision = decisions[item.key];

        if (!decision?.action) return false;

        if (decision.action === "create-new") {
          return canCreate;
        }

        return decision.associatedClientId !== null;
      }).length,
    [canCreate, decisions, items],
  );

  const canConfirm = items.length > 0 && completedCount === items.length;

  const updateDecision = (key: string, changes: Partial<DecisionState>) => {
    setDecisions((previous) => ({
      ...previous,
      [key]: {
        ...previous[key],
        ...changes,
      },
    }));
  };

  const handleConfirm = () => {
    if (!canConfirm) return;

    const result = items.reduce<AssociatedClientDecision[]>(
      (resolved, item) => {
        const decision = decisions[item.key];

        if (!decision?.action) return resolved;

        resolved.push({
          key: item.key,
          action: decision.action,
          associatedClientId:
            decision.action === "use-existing"
              ? decision.associatedClientId
              : null,
        });

        return resolved;
      },
      [],
    );

    onConfirm(result);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h5">Validar clientes asociados</Typography>

            <Typography variant="body2" color="text.secondary">
              Revisá las posibles coincidencias antes de crear los movimientos.
            </Typography>
          </Box>

          <IconButton onClick={onCancel} aria-label="Cerrar validación">
            <MdClose />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Se encontraron {items.length} clientes asociados que requieren
          confirmación.
        </Alert>

        <TableContainer sx={{ maxHeight: "52vh" }}>
          <Table stickyHeader sx={{ minWidth: 950 }}>
            <TableHead>
              <TableRow>
                <TableCell>Cliente</TableCell>
                <TableCell>Nombre en el Excel</TableCell>
                <TableCell>Coincidencias encontradas</TableCell>
                <TableCell>Decisión</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((item) => {
                const decision = decisions[item.key];
                const isNewAssociatedClient = item.candidates.length === 0;
                const highestScore = item.candidates[0]?.score ?? 0;
                const requiresDecision = !isNewAssociatedClient && !decision?.action;

                return (
                  <TableRow key={item.key}>
                    <TableCell>
                      <Typography fontWeight={600}>
                        {item.clientCode}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography>{item.importedName}</Typography>

                      <Typography variant="caption" color="text.secondary">
                        Afecta {item.affectedRecords}{" "}
                        {item.affectedRecords === 1 ? "movimiento" : "movimientos"}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      {isNewAssociatedClient ? (
                        <>
                          <Chip
                            size="small"
                            color="info"
                            label="Cliente nuevo"
                          />

                          <Typography
                            display="block"
                            variant="caption"
                            color="text.secondary"
                            sx={{ mt: 1 }}
                          >
                            No se encontraron coincidencias.
                          </Typography>
                        </>
                      ) : (
                        <>
                          <FormControl fullWidth size="small">
                            <Select
                              value={decision?.associatedClientId ?? ""}
                              onChange={(event) =>
                                updateDecision(item.key, {
                                  associatedClientId: Number(
                                    event.target.value,
                                  ),
                                })
                              }
                            >
                              {item.candidates.map((candidate) => (
                                <MenuItem
                                  key={candidate.associatedClient.id}
                                  value={candidate.associatedClient.id}
                                >
                                  {candidate.associatedClient.razon_social} ·{" "}
                                  {Math.round(candidate.score * 100)}%
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>

                            <Chip
                              size="small"
                              sx={{ mt: 1 }}
                              color={
                                highestScore >= 0.85 ? "success" : "warning"
                              }
                              label={
                                highestScore >= 0.85
                                  ? "Coincidencia alta"
                                  : "Requiere revisión"
                              }
                            />
                        </>
                      )}
                    </TableCell>

                    <TableCell>
                        {isNewAssociatedClient ? (
                          <Alert severity={canCreate ? "info" : "error"}>
                            {canCreate
                              ? `Se creará "${item.importedName}" al confirmar la importación.`
                              : "Tu rol no permite crear clientes asociados."}
                          </Alert>
                        ) : (
                          <RadioGroup
                            row
                            value={decision?.action ?? ""}
                            onChange={(event) =>
                              updateDecision(item.key, {
                                action: event.target
                                  .value as AssociatedClientDecision["action"],
                              })
                            }
                          >
                            <FormControlLabel
                              value="use-existing"
                              control={<Radio />}
                              label="Usar existente"
                            />

                            <FormControlLabel
                              value="create-new"
                              control={<Radio />}
                              label="Crear nuevo"
                              disabled={!canCreate}
                            />
                          </RadioGroup>
                        )}

                        {requiresDecision && (
                          <Typography color="error" variant="caption">
                            Seleccioná una opción para continuar.
                          </Typography>
                        )}

                        {!canCreate && !isNewAssociatedClient && (
                          <Typography color="error" variant="caption" display="block">
                            Tu rol no permite crear clientes asociados.
                          </Typography>
                        )}
                      </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: "divider",
          justifyContent: "space-between",
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="body2">
          <strong>
            {completedCount} de {items.length}
          </strong>{" "}
          decisiones completadas
        </Typography>

        <Box display="flex" gap={1}>
          <Button variant="outlined" onClick={onCancel}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            Continuar con la importación
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
